import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import {Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import * as React from 'react'
import {Suspense} from 'react'
import type {EntryPointProps} from 'react-relay'
import {EntryPointContainer, graphql, useFragment, usePreloadedQuery} from 'react-relay'
import type {PreloadedEntryPoint} from 'react-relay/relay-hooks/EntryPointTypes'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import AgentHistory from '../../../../containers/AgentHistory'
import {useQueryParam} from '../../../../hooks/routes'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import {HTTPCodesEnum} from '../../../../rest/errors/HTTPError'
import {getTabsArg} from '../../../../rest/tabs'
import {getAgentTypeHref, getHrefWithQueryParams} from '../../../../routes'
import {restApi} from '../../../../services/rest'
import {
  ClassicUICloudImageTabNamesEnum,
  OverviewCloudImageTabNamesEnum,
  stringifyId,
  toAgentTypeId,
  toTabId,
} from '../../../../types'
import type {AgentTypeId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {objectToQuery} from '../../../../utils/queryParams'
import {getTabParamsKey} from '../../../../utils/tabs'
import ClassicUITabAdapter from '../../../common/ClassicUITabAdapter/ClassicUITabAdapter'
import Loader from '../../../common/Loader/Loader'
import OverviewTab from '../../../common/OverviewTab/OverviewTab'
import tabStyles from '../../../common/OverviewTab/OverviewTab.css'
import Plugin from '../../../common/Plugin/Plugin'
import {useCanViewAgentDetails} from '../AgentsPages.hooks'

import type {AgentTypePage_agentType$key} from './__generated__/AgentTypePage_agentType.graphql'
import type {AgentTypePageQuery} from './__generated__/AgentTypePageQuery.graphql'
import {query} from './AgentTypePage.queries'
import type {AgentTypeParametersComponentType} from './AgentTypeParameters/AgentTypeParameters'
import type {AgentTypeParametersEntryPoint} from './AgentTypeParameters/AgentTypeParameters.entryPoint'
import {isNewCloudImageParametersTab} from './AgentTypeParameters/utils/isNewCloudImageParametersTab'

import styles from './AgentTypePage.css'

const IFRAME_TABS = [
  OverviewCloudImageTabNamesEnum.SUMMARY,
  OverviewCloudImageTabNamesEnum.HISTORY,
  OverviewCloudImageTabNamesEnum.COMPATIBLE_CONFIGUTATIONS,
  OverviewCloudImageTabNamesEnum.BUILD_RUNNERS,
  OverviewCloudImageTabNamesEnum.AGENT_PARAMETERS,
]

const fragment = graphql`
  fragment AgentTypePage_agentType on AgentTypeRest {
    name
  }
`

type Props = {
  agentTypeId: AgentTypeId
  agentTypeKey: AgentTypePage_agentType$key | null
  parametersTabEntryPoint?: PreloadedEntryPoint<AgentTypeParametersComponentType>
}

function AgentTypePage({agentTypeId, agentTypeKey, parametersTabEntryPoint}: Props) {
  const agentType = useFragment(fragment, agentTypeKey)

  const tabParam = useQueryParam('tab')
  const tab = tabParam != null ? toTabId(tabParam) : OverviewCloudImageTabNamesEnum.SUMMARY
  const kind = useQueryParam('kind')
  const location = useLocation()
  const navigate = useNavigate()
  const tabParamsKey = getTabParamsKey({
    agentTypeId,
  })
  const {tabs} = restApi.endpoints.getTabs.useQuery(getTabsArg(tabParamsKey, true, false), {
    selectFromResult: ({data}) => ({tabs: data ?? emptyArray}),
  })
  const onSelect = React.useCallback(
    (selectedTab: string) => {
      navigate(
        getHrefWithQueryParams(location, null, () => ({
          tab: selectedTab,
        })),
      )
    },
    [navigate, location],
  )

  return (
    <div className={styles.page}>
      <H1>{agentType?.name}</H1>
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
      <Tabs
        autoCollapse={process.env.NODE_ENV !== 'test'}
        className={tabStyles.tabs}
        selected={tab}
        onSelect={onSelect}
        key={stringifyId(agentTypeId)}
      >
        {tabs.map(({id, title}) => {
          const tabId = ClassicUICloudImageTabNamesEnum[stringifyId(id)]
          const tabHref = `${getAgentTypeHref(agentTypeId)}?${objectToQuery({
            tab: stringifyId(tabId),
          })}`

          if (tabId === OverviewCloudImageTabNamesEnum.HISTORY) {
            return (
              <OverviewTab id={tabId} key={stringifyId(tabId)} title={title} href={tabHref}>
                <AgentHistory
                  agent={{
                    typeId: agentTypeId,
                  }}
                  withAgentPatternFilter
                />
              </OverviewTab>
            )
          }

          if (
            tabId === OverviewCloudImageTabNamesEnum.AGENT_PARAMETERS &&
            isNewCloudImageParametersTab()
          ) {
            return (
              <OverviewTab id={tabId} key={stringifyId(tabId)} title={title} href={tabHref}>
                {parametersTabEntryPoint && (
                  <Suspense fallback={<Loader />}>
                    <EntryPointContainer entryPointReference={parametersTabEntryPoint} props={{}} />
                  </Suspense>
                )}
              </OverviewTab>
            )
          }

          return tabId != null && IFRAME_TABS.includes(tabId) ? (
            <OverviewTab isClassic id={tabId} key={tabId} title={title} href={tabHref}>
              <ClassicUITabAdapter agentTypeId={agentTypeId} tabId={id} title={title} kind={kind} />
            </OverviewTab>
          ) : null
        })}
      </Tabs>
    </div>
  )
}

const FORBIDDEN_ERROR = {statusCode: HTTPCodesEnum.FORBIDDEN, message: ''}

type Params = {
  agentTypeId: string
}
type Queries = {main: AgentTypePageQuery}
export type NestedEntryPoints = {parametersTab?: typeof AgentTypeParametersEntryPoint}
export default function AgentTypePageContainer({
  queries,
  entryPoints,
}: EntryPointProps<Queries, NestedEntryPoints, {}, {}>) {
  const params = useParams<Params>()
  const agentTypeId = toAgentTypeId(params.agentTypeId ?? '')
  const data = usePreloadedQuery(query, queries.main)
  const canViewAgentDetails = useCanViewAgentDetails(data)

  if (!canViewAgentDetails) {
    throw FORBIDDEN_ERROR
  }

  return (
    <AgentTypePage
      agentTypeKey={data.agentType ?? null}
      key={params.agentTypeId}
      agentTypeId={agentTypeId}
      parametersTabEntryPoint={entryPoints.parametersTab}
    />
  )
}
export type AgentTypePageContainerComponent = typeof AgentTypePageContainer
